/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
    Button,
    Collapse,
    FormControl,
    FormGroup,
    Grid2,
    InputLabel,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import { textFieldStyles } from '../../Configurations/ExpenseEntryConfig';

const styles = (theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        drawer: {
            width: '28em',
            padding: '1em',
            position: 'initial',
            '&:hover': {
                backgroundColor: 'transparent',
            },
            '&:focus': {
                backgroundColor: 'transparent',
            },
            '&:active': {
                backgroundColor: 'transparent',
            },
            borderRadius: '0',
        },
        content: {
            paddingLeft: '1em',
        },
        datePicker: {
            width: '10em',
            ...baseStyles.root,
            '& .MuiIconButton-root': {
                color: COLORS.LT_JOURNEY,
            },
        },
        icon: {
            color: COLORS.LT_JOURNEY,
        },
        clear: {
            textDecoration: 'underline',
            paddingLeft: '1em',
        },
        spacing: {
            paddingRight: '1em',
        },
        ...baseStyles,
        inputRoot: {
            ...baseStyles.inputRoot,
            '&$disabled .MuiIconButton-root': {
                background: COLORS.LT_MIDNIGHTBG2,
                color: COLORS.LT_MIDNIGHT33,
                borderRadius: 0,
            },
        },
    };
};

const ExpenseFilterDate = forwardRef((props, ref) => {
    const { classes, filterName, displayStartDate, displayEndDate } = props;
    const { control, watch, setValue, setError, clearErrors } = useFormContext();
    const startDate = watch(displayStartDate);
    const endDate = watch(displayEndDate);
    const name = filterName.replace(/ /g, '-');
    const [expanded, setExpanded] = useState(false);
    const [clear, setClear] = useState(false);
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);
    const [dateError, setDateError] = useState('');

    const format = 'YYYY-MM-DD';

    useImperativeHandle(ref, () => ({
        clearDateError: () => setDateError(''),
    }));

    const formatWeekEndingDate = (date, type) => {
        if (!date) {
            setValue(type, null);
            return;
        }
        const newDate = moment(date).format(format);
        setValue(type, newDate);
    };

    const handleDateChange = (date, type) => {
        setDateError('');
        formatWeekEndingDate(date, type); // Always set value

        if (date && type === displayStartDate && endDate && moment(date).isAfter(moment(endDate))) {
            setDateError('Start date cannot be greater than end date');
            setError(displayStartDate, {
                type: 'manual',
                message: 'Start date cannot be greater than end date',
            });
        } else if (
            date &&
            type === displayEndDate &&
            startDate &&
            moment(date).isBefore(moment(startDate))
        ) {
            setDateError('End date cannot be less than start date');
            setError(displayEndDate, {
                type: 'manual',
                message: 'End date cannot be less than start date',
            });
        } else {
            clearErrors([displayStartDate, displayEndDate]);
        }
    };

    const renderDateFilter = () => (
        <FormControl component="fieldset">
            <FormGroup>
                <Grid2 container>
                    <Grid2 item flex={1} className={classes.spacing}>
                        <Controller
                            name={displayStartDate}
                            control={control}
                            defaultValue={null}
                            render={({ field, onChange, value }) => (
                                <Grid2 item>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <Grid2
                                            container
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            spacing={2}
                                        >
                                            <Grid2 item flex={1}>
                                                <InputLabel shrink focused={fromFocused}>
                                                    From
                                                </InputLabel>
                                                <DatePicker
                                                    sx={(theme) => ({
                                                        '& .MuiPickersInputBase-root': {
                                                            marginTop: 0,
                                                        },
                                                    })}
                                                    className={classes.datePicker}
                                                    disableToolbar
                                                    variant="inline"
                                                    inputFormat="MM/dd/yyyy"
                                                    id={`${name}-inline`}
                                                    value={startDate ? moment(startDate) : null}
                                                    onChange={(e) =>
                                                        handleDateChange(e, displayStartDate)
                                                    }
                                                    slotProps={{
                                                        popper: {
                                                            sx: { zIndex: 1320 }, // Adjust zIndex
                                                            onFocus: () => setFromFocused(true),
                                                        },
                                                        dialog: {
                                                            sx: { zIndex: 1320 }, // For dialog mode on mobile
                                                        },
                                                        textField: {
                                                            placeholder: 'mm/dd/yyyy',
                                                            variant: 'standard',
                                                            disableUnderline: true,
                                                            margin: 'none',
                                                            borderRadius: 4,
                                                            border: '1px solid',
                                                            onFocus: () => setFromFocused(true),
                                                            onBlur: () => setFromFocused(false),
                                                            InputLabelProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                            InputProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                            SelectProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                        },
                                                        openPickerButton: {
                                                            'aria-label':
                                                                props.label || 'Choose date',
                                                        },
                                                    }}
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </LocalizationProvider>
                                </Grid2>
                            )}
                        />
                    </Grid2>
                    <Grid2 item flex={1}>
                        <Controller
                            name={displayEndDate}
                            control={control}
                            defaultValue={null}
                            render={({ field, onChange, value }) => (
                                <Grid2 ite flex={1}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <Grid2
                                            container
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            spacing={2}
                                            flex={1}
                                        >
                                            <Grid2 item flex={1}>
                                                <InputLabel shrink focused={toFocused}>
                                                    To
                                                </InputLabel>
                                                <DatePicker
                                                    sx={(theme) => ({
                                                        '& .MuiPickersInputBase-root': {
                                                            marginTop: 0,
                                                        },
                                                    })}
                                                    className={classes.datePicker}
                                                    disableToolbar
                                                    variant="inline"
                                                    inputFormat="MM/dd/yyyy"
                                                    id={`${name}-inline`}
                                                    value={endDate ? moment(endDate) : null}
                                                    onChange={(e) => {
                                                        handleDateChange(e, displayEndDate);
                                                    }}
                                                    slotProps={{
                                                        popper: {
                                                            sx: { zIndex: 1320 }, // Adjust zIndex
                                                            onFocus: () => setToFocused(true),
                                                        },
                                                        dialog: {
                                                            sx: { zIndex: 1320 }, // For dialog mode on mobile
                                                        },
                                                        textField: {
                                                            placeholder: 'mm/dd/yyyy',
                                                            variant: 'standard',
                                                            disableUnderline: true,
                                                            margin: 'none',
                                                            borderRadius: 4,
                                                            onFocus: () => setToFocused(true),
                                                            onBlur: () => setToFocused(false),
                                                            border: '1px solid',
                                                            InputLabelProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                            InputProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                            SelectProps: {
                                                                shrink: true,
                                                                disableUnderline: true,
                                                            },
                                                        },
                                                        openPickerButton: {
                                                            'aria-label':
                                                                props.label || 'Choose date',
                                                        },
                                                    }}
                                                />
                                            </Grid2>
                                        </Grid2>
                                    </LocalizationProvider>
                                </Grid2>
                            )}
                        />
                    </Grid2>
                </Grid2>
                {dateError && (
                    <Typography className={classes.error} color="error">
                        {dateError}
                    </Typography>
                )}
            </FormGroup>
        </FormControl>
    );

    const renderAccordian = () => (
        <>
            <Button
                className={classes.drawer}
                style={
                    !expanded
                        ? { borderBottom: `1px solid ${COLORS.LT_MIDNIGHTSTEEL}` }
                        : { borderBottom: 'none' }
                }
                disableRipple
                onClick={() => {
                    setExpanded(!expanded);
                    setClear(!clear);
                }}
                aria-expanded={expanded}
                aria-controls="panel1-content"
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
                <Grid2 container alignItems="space-between" flex={1} spacing={1}>
                    <Grid2 item>
                        <Typography>
                            {filterName} ({startDate || endDate ? 1 : 0})
                        </Typography>
                    </Grid2>

                    {clear && (
                        <Grid2 item>
                            <Typography
                                variant="body2"
                                className={classes.clear}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setValue(displayStartDate, null);
                                    setValue(displayEndDate, null);
                                    setDateError('');
                                    clearErrors([displayStartDate, displayEndDate]);
                                }}
                            >
                                Clear
                            </Typography>
                        </Grid2>
                    )}
                </Grid2>
            </Button>
            <Collapse in={expanded} className={classes.content}>
                {renderDateFilter()}
            </Collapse>
        </>
    );

    return <> {renderAccordian()} </>;
});

export default withStyles(styles)(ExpenseFilterDate);
